/* ============================================================================
   G2M — Variant table links + sticky add-to-cart
   ----------------------------------------------------------------------------
   Loaded on product pages that render the variant table (any product with more
   than one variant) or the sticky bar (still tagged `variant-table` only).

   Both custom elements drive the THEME's own machinery rather than
   re-implementing it:

   <g2m-variant-table>
     A row link sets the matching inputs inside the theme's <variant-picker>
     and fires ONE `change`. The theme's VariantPicker then collects the full
     option-value set, re-renders the product block, updates the URL and emits
     `variant:change` / `variant:changed`. Nothing about pricing, availability,
     gallery or buy buttons is duplicated here.
     If the picker cannot be found the click is left alone, so the anchor's
     href (?variant=ID) performs a normal page load instead.

   <g2m-sticky-atc>
     Forwards its click to the real Add to cart button so the theme's
     <buy-button> handler runs untouched (it needs to find the enclosing form).
     Visibility is driven by an IntersectionObserver on that same button.
   ========================================================================== */
(function () {
  'use strict';

  var OPTION_SEPARATOR = '~|~';

  /* ---------------------------------------------------------------- helpers */

  function getFormId(el) {
    return el.getAttribute('form-id') || '';
  }

  function getForm(el) {
    var id = getFormId(el);
    return id ? document.getElementById(id) : null;
  }

  /* NOTE: never read the form element's ".id" property here. On an
     HTMLFormElement, named form controls shadow element properties, and the
     product form contains <input name="id"> for the variant id (theme.js
     itself relies on that: it does form.id.value = variantId). So reading
     form.id returns that INPUT element, not the id string, and a selector
     built from it silently matches nothing. The form-id attribute on our own
     custom element is the reliable source. */
  function getVariantPicker(formId) {
    if (!formId) return null;
    var picker = document.querySelector('variant-picker[form-id="' + formId + '"]');
    /* Some layouts render a single picker without a matching form-id. */
    if (!picker) picker = document.querySelector('variant-picker');
    return picker;
  }

  function realAddToCartButton(form) {
    if (!form) return null;
    return form.querySelector('buy-button .product-form__add-button, .product-form__add-button');
  }

  /* ------------------------------------------------- <g2m-variant-table> */

  function selectVariantByOptions(formId, optionValues) {
    var picker = getVariantPicker(formId);
    if (!picker) return false;

    var touched = 0;
    var lastControl = null;

    for (var i = 0; i < optionValues.length; i++) {
      var position = i + 1;
      var value = optionValues[i];
      if (value === null || value === undefined || value === '') continue;

      /* Radio / swatch inputs */
      var radios = picker.querySelectorAll('input[data-option-position="' + position + '"]');
      var matched = false;

      for (var r = 0; r < radios.length; r++) {
        if (radios[r].value === value) {
          if (!radios[r].checked) radios[r].checked = true;
          lastControl = radios[r];
          matched = true;
          touched++;
          break;
        }
      }

      if (matched) continue;

      /* Dropdown selects */
      var select = picker.querySelector('select[data-option-position="' + position + '"]');
      if (select) {
        for (var o = 0; o < select.options.length; o++) {
          if (select.options[o].value === value) {
            select.selectedIndex = o;
            lastControl = select;
            touched++;
            break;
          }
        }
      }
    }

    if (!touched || !lastControl) return false;

    /* One change event only: the theme reads every option control's current
       value from the form, so intermediate events would just cause extra
       section renders for combinations the user never asked for. */
    lastControl.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function markSelectedRow(root, variantId) {
    var rows = root.querySelectorAll('.g2m-vt__row');
    for (var i = 0; i < rows.length; i++) {
      var isMatch = rows[i].getAttribute('data-variant-id') === String(variantId);
      rows[i].classList.toggle('is-selected', isMatch);
      if (isMatch) {
        rows[i].setAttribute('aria-current', 'true');
      } else {
        rows[i].removeAttribute('aria-current');
      }
    }
  }

  function initVariantTable(root) {
    if (root.dataset.g2mBound === '1') return;
    root.dataset.g2mBound = '1';

    var form = getForm(root);
    var formId = getFormId(root);

    /* Delegated so the handler survives the theme replacing table markup. */
    root.addEventListener('click', function (event) {
      var link = event.target.closest ? event.target.closest('[data-g2m-variant-link]') : null;
      if (!link) return;

      var raw = link.getAttribute('data-variant-options') || '';
      var optionValues = raw ? raw.split(OPTION_SEPARATOR) : [];
      if (!optionValues.length) return;

      if (selectVariantByOptions(formId, optionValues)) {
        event.preventDefault();
        markSelectedRow(root, link.getAttribute('data-variant-id'));

        /* Bring the buy area back into view — the table sits below it. */
        var target = realAddToCartButton(form);
        if (target) {
          var box = target.getBoundingClientRect();
          if (box.top < 0 || box.bottom > window.innerHeight) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
      /* else: no picker found — let the href navigate normally. */
    });

    /* Keep the highlighted row in step when the variant changes elsewhere
       (option swatches, dropdowns, back/forward). */
    if (form) {
      form.addEventListener('variant:changed', function (event) {
        var variant = event.detail && event.detail.variant;
        if (variant && variant.id) markSelectedRow(root, variant.id);
      });
    }
  }

  /* --------------------------------------------------- <g2m-sticky-atc> */

  function initStickyAtc(bar) {
    if (bar.dataset.g2mBound === '1') return;
    bar.dataset.g2mBound = '1';

    var form = getForm(bar);
    var button = bar.querySelector('[data-g2m-satc-button]');
    var priceEl = bar.querySelector('[data-g2m-satc-price]');
    var variantEl = bar.querySelector('[data-g2m-satc-variant]');
    if (!form || !button) return;

    var mirrored = null;        /* the native button we are currently mirroring */
    var stateObserver = null;
    var revealObserver = null;
    var syncQueued = false;

    /* The NATIVE button is the single source of truth for availability. We do not
       decide sold-out/unavailable ourselves — whatever reason the theme has for
       disabling it (sold out, variant unavailable, no variant selected, or the
       transient disable while an add-to-cart request is in flight) is mirrored
       verbatim. */
    function nativeIsDisabled(real) {
      if (!real) return true;
      return real.disabled === true ||
        real.hasAttribute('disabled') ||
        real.getAttribute('aria-disabled') === 'true' ||
        real.classList.contains('button--disabled');
    }

    function applyDisabled(disabled) {
      button.disabled = disabled;
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      /* Keep it out of the tab order while unusable, and let CSS grey it out. */
      if (disabled) {
        button.setAttribute('tabindex', '-1');
      } else {
        button.removeAttribute('tabindex');
      }
      bar.classList.toggle('is-disabled', disabled);
    }

    function syncFromNative() {
      var real = realAddToCartButton(form);
      applyDisabled(nativeIsDisabled(real));

      /* Mirror the native button's own label, so the wording and its translation
         come from the theme ("Sold out", "Unavailable", "Pre-order", …) rather
         than being guessed here. */
      if (real) {
        var label = (real.textContent || '').replace(/\s+/g, ' ').trim();
        if (label) button.textContent = label;
      }

      /* Price straight from the theme's rendered markup — no money formatting
         duplicated in JS. */
      if (priceEl) {
        var themePrice = form.querySelector('.price-list .price, .price');
        if (themePrice) {
          var text = themePrice.textContent.replace(/\s+/g, ' ').trim();
          if (text) priceEl.textContent = text;
        }
      }

      /* The theme replaces the buy-buttons markup on variant change, so both
         observers have to follow the new element. */
      if (real && real !== mirrored) {
        mirrored = real;
        attachRevealObserver(real);
      }
    }

    /* Coalesce bursts of mutations into one sync per frame. */
    function queueSync() {
      if (syncQueued) return;
      syncQueued = true;
      var run = function () { syncQueued = false; syncFromNative(); };
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(run);
      } else {
        window.setTimeout(run, 16);
      }
    }

    /* Watch the buy-buttons area for disabled/aria-disabled/class changes AND for
       the element being swapped out. This is what keeps the sticky button in step
       no matter when the theme flips the state — including after
       `variant:changed` has already fired and the block is re-rendered. */
    function attachStateObserver() {
      if (stateObserver) stateObserver.disconnect();
      if (typeof MutationObserver === 'undefined') return;
      var scope = form.querySelector('.product-form__buy-buttons') || form;
      stateObserver = new MutationObserver(queueSync);
      stateObserver.observe(scope, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class']
      });
    }

    function attachRevealObserver(anchor) {
      if (typeof IntersectionObserver === 'undefined') {
        bar.hidden = false;
        bar.classList.add('is-visible');
        return;
      }
      if (revealObserver) revealObserver.disconnect();
      revealObserver = new IntersectionObserver(function (entries) {
        var visible = entries[0].isIntersecting;
        bar.hidden = visible;
        bar.classList.toggle('is-visible', !visible);
      }, { rootMargin: '0px 0px -40px 0px', threshold: 0 });
      revealObserver.observe(anchor);
    }

    /* Click forwarding — the theme's <buy-button> performs the actual add, so
       quantity, selected variant, validation, AJAX cart and the drawer all stay
       with the theme. A disabled button is already inert to mouse, keyboard and
       element.click(), but the state is re-checked here so nothing can slip
       through if markup and state ever disagree. */
    button.addEventListener('click', function (event) {
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      var real = realAddToCartButton(form);
      if (nativeIsDisabled(real)) {
        event.preventDefault();
        event.stopPropagation();
        syncFromNative();   /* markup was stale — bring the sticky state in line */
        return;
      }

      real.click();

      /* The theme disables the native button for the duration of its request; the
         MutationObserver mirrors that immediately, so rapid repeat clicks land on
         an already-disabled sticky button and cannot queue a second add. */
      queueSync();
    });

    /* variant:changed still drives the variant label, and triggers a sync as a
       belt-and-braces companion to the MutationObserver. */
    form.addEventListener('variant:changed', function (event) {
      var variant = event.detail && event.detail.variant;
      if (variantEl && variant && variant.title) variantEl.textContent = variant.title;
      queueSync();
    });

    document.addEventListener('product:rerender', queueSync);

    attachStateObserver();
    syncFromNative();

    var anchor = realAddToCartButton(form);
    if (anchor) {
      mirrored = anchor;
      attachRevealObserver(anchor);
    } else {
      bar.hidden = false;
      bar.classList.add('is-visible');
    }
  }

  /* ------------------------------------------------------------- bootstrap */

  function initAll() {
    var tables = document.querySelectorAll('g2m-variant-table');
    for (var i = 0; i < tables.length; i++) initVariantTable(tables[i]);

    var bars = document.querySelectorAll('g2m-sticky-atc');
    for (var b = 0; b < bars.length; b++) initStickyAtc(bars[b]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* The theme re-renders product blocks on variant change and on section load
     in the editor; re-bind whatever appeared. */
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('product:rerender', initAll);
})();
